import React, { useState } from 'react';
import axios from 'axios';
import { RefreshCw } from 'lucide-react';
import { T, feldStil, knopfStil } from '../../lib/designTokens';
import { fehlerText, normalisiereDealId, zusammenfassung } from '../../lib/dokumentenAssistent';
import { AuftragErgebnis, Hinweis, StatusMarke } from '../contracts/dokumentenAssistent/AuftragDetails';

// Dokumenten-Automatik (seit 21.10.2026, Ersatz fuer dokassi-auto): Probelauf
// eines Deals aus der Admin-Uebersicht. Der Server arbeitet wie die Automatik
// an "Vertrag Digital vorhanden" - Vertrag aus dem Deal, Anlagen erkennen,
// Ernsthaftigkeitsnachweis -, schreibt aber nichts nach Bitrix und prueft die
// Phase nicht. Laeuft auch abgeschaltet (Testknopf, siehe AGENTS.md).

// Ein Auftrag darf am Server 200 s dauern; dazu kommen Download und Antwort.
const ZEITGRENZE_MS = 290000;

export default function DokumentenAutomatikTest({ apiBase, session, onFertig }) {
  const [dealEingabe, setDealEingabe] = useState('');
  const [laeuft, setLaeuft] = useState(false);
  const [auftrag, setAuftrag] = useState(null);
  const [fehler, setFehler] = useState('');

  const dealId = normalisiereDealId(dealEingabe);
  const bereit = Boolean(dealId) && !laeuft;
  // Der Test ist immer ein Probelauf - so sagen Nachweis und Phase "würde".
  const probe = auftrag ? { ...auftrag, nurAnalyse: true } : null;

  const probelauf = async () => {
    if (!dealId) {
      setFehler('Bitte eine gültige Deal-ID eingeben – nur Ziffern, z. B. 12345 oder D_12345.');
      return;
    }
    setLaeuft(true);
    setAuftrag(null);
    setFehler('');
    try {
      const { data } = await axios.post(
        `${apiBase}/automation/dokumenten-automatik`,
        { op: 'test', dealId },
        { headers: session?.access_token ? { Authorization: `Bearer ${session.access_token}` } : {}, timeout: ZEITGRENZE_MS },
      );
      if (!data?.auftrag) throw new Error('Die Antwort enthielt kein Ergebnis.');
      setAuftrag(data.auftrag);
      onFertig?.();
    } catch (error) {
      setFehler(fehlerText(error, 'Probelauf fehlgeschlagen.'));
    } finally {
      setLaeuft(false);
    }
  };

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
      <div style={{ fontSize: T.tMeta, color: T.body, lineHeight: 1.6 }}>
        <strong style={{ color: T.ink }}>Probelauf für einen Deal.</strong> Holt den Vertrag aus dem Deal, lässt die
        Bitrix-KI die Seiten erkennen und zeigt, was abgelegt würde – samt Ernsthaftigkeitsnachweis und ob der Deal
        nach „NAP starten“ ginge. Es wird nichts in Bitrix geschrieben, die Phase wird nicht geprüft. Dauert bei
        Scans bis zu drei Minuten.
      </div>
      <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', alignItems: 'center' }}>
        <input
          value={dealEingabe}
          onChange={(e) => setDealEingabe(e.target.value)}
          onKeyDown={(e) => { if (e.key === 'Enter' && bereit) probelauf(); }}
          placeholder="Deal-ID, z. B. 12016"
          aria-label="Deal-ID für den Probelauf"
          inputMode="numeric"
          disabled={laeuft}
          style={{ ...feldStil, width: 200 }}
        />
        <button type="button" onClick={probelauf} disabled={!bereit}
          style={{ ...knopfStil(true), opacity: bereit ? 1 : 0.6 }}>
          <RefreshCw size={13} style={laeuft ? { animation: 'spin 1s linear infinite' } : undefined} />
          {laeuft ? 'Prüft …' : 'Probelauf'}
        </button>
      </div>
      {dealEingabe.trim() && !dealId ? (
        <div style={{ fontSize: T.tMikro, color: T.gefahr, fontWeight: 600 }}>Eine Deal-ID besteht nur aus Ziffern.</div>
      ) : null}

      {fehler ? <Hinweis ton="gefahr">{fehler}</Hinweis> : null}

      {probe ? (
        <div style={{ display: 'grid', gap: 10, borderTop: `1px solid ${T.hairline}`, paddingTop: 12 }}>
          <div style={{ display: 'flex', gap: 8, alignItems: 'baseline', flexWrap: 'wrap', fontSize: T.tMeta, color: T.body }}>
            <StatusMarke status={probe.status} />
            <span style={{ fontWeight: 700, color: T.ink }}>
              Deal {probe.dealId || dealId}{probe.dealTitel ? ` · ${probe.dealTitel}` : ''}
            </span>
            <span>{zusammenfassung(probe)}</span>
          </div>
          <AuftragErgebnis auftrag={probe} mitPhase />
        </div>
      ) : null}
    </div>
  );
}
